/**
 * Снятие реальной меты с отрисованной страницы.
 *
 * Берём именно то, что оказалось в DOM: мету собирают несколько источников
 * (meta_tags, шаблоны каталога, редакс-стор страницы), и разойтись они могут
 * незаметно. Данные уходят в nestFarkopDatabase, а панель показывает их в
 * разделе «SEO → Мониторинг меты».
 */

/** Мета страницы в том виде, в котором её принимает сервис */
export type CollectedPageMeta = {
  url: string;
  domain: string;
  title: string;
  description: string;
  h1: string;
  ogTitle: string;
  ogDescription: string;
  canonical: string;
  robots: string;
};

/** Значение тега: схлопнутые пробелы и ограничение длины */
function normalizeValue(value: unknown): string {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 2000);
}

/** Адрес страницы без слешей по краям и без параметров; пусто — главная */
export function toTrackedUrl(pathname: unknown): string {
  return String(pathname ?? "")
    .split("#")[0]
    .split("?")[0]
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

/** Хост без www: farkop.ru, msk.farkop.ru */
export function toTrackedDomain(hostname: unknown): string {
  return String(hostname ?? "")
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");
}

function attributeOf(
  doc: Document,
  selector: string,
  attribute: string,
): string {
  const element = doc.querySelector(selector);
  return normalizeValue(element?.getAttribute(attribute) ?? "");
}

/**
 * Мета текущей страницы. `location` передаётся отдельно: в SPA переход
 * происходит раньше, чем обновится Head, и адрес нужно брать на момент съёма.
 */
export function collectPageMeta(
  doc: Document,
  location: { pathname: string; hostname: string },
): CollectedPageMeta {
  return {
    url: toTrackedUrl(location?.pathname),
    domain: toTrackedDomain(location?.hostname),
    title: normalizeValue(doc?.title),
    description: attributeOf(doc, 'meta[name="description"]', "content"),
    h1: normalizeValue(doc?.querySelector("h1")?.textContent ?? ""),
    ogTitle: attributeOf(doc, 'meta[property="og:title"]', "content"),
    ogDescription: attributeOf(
      doc,
      'meta[property="og:description"]',
      "content",
    ),
    canonical: attributeOf(doc, 'link[rel="canonical"]', "href"),
    robots: attributeOf(doc, 'meta[name="robots"]', "content"),
  };
}

/** Ключ для сравнения снимков: одинаковый — значит отправлять нечего */
export function metaSnapshotKey(meta: CollectedPageMeta): string {
  return [
    meta.domain,
    meta.url,
    meta.title,
    meta.description,
    meta.h1,
    meta.ogTitle,
    meta.ogDescription,
    meta.canonical,
    meta.robots,
  ].join("|");
}
